import React, {
	ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { Button, FloatLabelInput, Modal } from '@buildbox/components'
import ReactMultiSelectCheckboxes from 'react-multiselect-checkboxes'
import { ISelect } from 'shared/interfaces/select'
import { IProps } from './types'
import { Container } from './styles'
import {
	primaryDashboardButtonStyles,
	primaryInputStyles,
	reactSelectStyles,
} from 'shared/styles/theme'
import { IImageUpload } from 'shared/components/ImageUpload/types'
import ImageUpload from 'shared/components/ImageUpload'
import { createBanner, updateBanner } from 'shared/services/banner.service'
import { uploadToS3 } from 'shared/services/aws.service'
import cogoToast from 'cogo-toast'
import cogoDefaultOptions from 'shared/util/toaster'
import { getDropdownButtonLabelOverride } from 'shared/util/multiSelectCheckboxUtils'

const SELECT_ALL_VALUE = '*'

const selectAllOption: ISelect = {
	value: SELECT_ALL_VALUE,
	label: 'Todas as Filiais',
}

const isSelectAllOption = (option: ISelect) => option.value === SELECT_ALL_VALUE

function ModalBanner(props: IProps) {
	const {
		isActive,
		selectedBanner,
		closeModal,
		retailOptions,
		selectedRetail,
		handleChangeRetail,
		handleChangeModalRetail,
		modalSelectedRetails,
	} = props

	const [image, setImage] = useState<IImageUpload>({
		file: null,
		url: '',
	})
	const [title, setTitle] = useState('')
	const [redirectLink, setRedirectLink] = useState('')
	const [isSubmiting, setIsSubmiting] = useState(false)
	const [isFormValid, setIsFormValid] = useState(false)

	const userRetailOptions = useMemo<ISelect[]>(
		() =>
			retailOptions.map(({ value, label }) => ({
				value,
				label,
			})),
		[retailOptions],
	)

	const userRetailOptionsWithAllRetails = useMemo<ISelect[]>(
		() => [selectAllOption, ...userRetailOptions],
		[userRetailOptions],
	)

	const handleMultiSelectChange = useCallback(
		(selectedOptions: ISelect[]) => {
			const hasSelectAll = selectedOptions.some(isSelectAllOption)
			const hadSelectAll = modalSelectedRetails.some(isSelectAllOption)

			if (hasSelectAll !== hadSelectAll) {
				handleChangeModalRetail(
					hasSelectAll ? userRetailOptionsWithAllRetails : [],
				)
				return
			}

			const individualOptions = selectedOptions.filter(
				(opt) => !isSelectAllOption(opt),
			)

			const allIndividualsSelected =
				individualOptions.length === userRetailOptions.length

			handleChangeModalRetail(
				allIndividualsSelected
					? userRetailOptionsWithAllRetails
					: individualOptions,
			)
		},
		[
			modalSelectedRetails,
			userRetailOptions,
			userRetailOptionsWithAllRetails,
			handleChangeModalRetail,
		],
	)

	function loadBannerState() {
		if (!isActive) return

		if (selectedBanner) {
			setImage({ file: null, url: selectedBanner.image })
			setTitle(selectedBanner.title)
			setRedirectLink(selectedBanner.redirectLink)

			const bannerRetailIds =
				selectedBanner.retail?.map(({ retailId }) => retailId) ?? []
			const matchedRetails = userRetailOptions.filter(({ value }) =>
				bannerRetailIds.includes(value),
			)

			const hasAllRetailsSelected =
				userRetailOptions.length > 0 &&
				matchedRetails.length === userRetailOptions.length

			handleChangeModalRetail(
				hasAllRetailsSelected
					? userRetailOptionsWithAllRetails
					: matchedRetails,
			)
		}
	}

	function validateForm() {
		const isValid =
			(!!image.file || !!image.url) &&
			!!title &&
			!!redirectLink &&
			!!selectedRetail

		setIsFormValid(isValid)
	}

	function clearFiels() {
		setImage({ file: null, url: '' })
		setTitle('')
		setRedirectLink('')
	}

	const onClose = useCallback(
		(refetchData: boolean = false) => {
			clearFiels()
			closeModal({ refetchData, dataToFetch: ['banner'] })
		},
		[closeModal],
	)

	const handleChangeImage = useCallback((value: IImageUpload) => {
		setImage(value)
	}, [])

	const handleChangeTitle = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setTitle(event.target.value)
		},
		[],
	)

	const handleChangeRedirectLink = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setRedirectLink(event.target.value)
		},
		[],
	)

	const handleSubmit = useCallback(() => {
		;(async () => {
			if (!isFormValid || !selectedRetail?.value) return

			try {
				setIsSubmiting(true)

				const imageUrl = image.file ? await uploadToS3(image.file) : image.url

				const listRetailsSelected = modalSelectedRetails
					.filter((retail) => !isSelectAllOption(retail))
					.map((retail) => retail.value)

				const bannerToSave = {
					...selectedBanner,
					image: imageUrl,
					title,
					redirectLink,
					retailIds: listRetailsSelected,
				}

				if (!selectedBanner) {
					await createBanner(bannerToSave)
				} else {
					await updateBanner(bannerToSave)
				}

				cogoToast.success('Banner salvo com sucesso!', cogoDefaultOptions)
			} finally {
				handleChangeRetail(retailOptions[0])

				setIsSubmiting(false)
				onClose(true)
			}
		})()
	}, [
		isFormValid,
		selectedRetail,
		image,
		modalSelectedRetails,
		selectedBanner,
		title,
		redirectLink,
		handleChangeRetail,
		retailOptions,
		onClose,
	])

	useEffect(validateForm, [image, title, redirectLink, selectedRetail])
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(loadBannerState, [selectedBanner, isActive, userRetailOptions])

	return (
		<Modal
			isActive={isActive}
			title={!selectedBanner ? 'Novo Banner' : 'Detalhes do Bannes'}
			onClose={() => onClose()}
			className='modal-banner'
			size={600}
		>
			<Container>
				<div className='input-container select-retail'>
					<ReactMultiSelectCheckboxes
						isMulti
						options={userRetailOptionsWithAllRetails}
						value={modalSelectedRetails}
						onChange={handleMultiSelectChange}
						getDropdownButtonLabel={getDropdownButtonLabelOverride}
						placeholderButtonLabel='Loja'
						placeholder='Buscar Loja'
						noOptionsMessage={() => 'Nenhuma loja para mostrar.'}
						{...primaryInputStyles}
						styles={reactSelectStyles}
					/>
				</div>

				<ImageUpload returnImage={handleChangeImage} imageUrl={image.url} />

				<div className='inputs-group'>
					<div className='input-container'>
						<FloatLabelInput
							{...primaryInputStyles}
							label='Título do Banner'
							round
							id='title'
							onChange={handleChangeTitle}
							value={title}
							maxLength={50}
						/>
					</div>
					<div className='input-container'>
						<FloatLabelInput
							{...primaryInputStyles}
							label='Link de Redirecionamento'
							round
							id='email'
							onChange={handleChangeRedirectLink}
							value={redirectLink}
						/>
					</div>
				</div>

				<Button
					{...primaryDashboardButtonStyles}
					className='submit-button'
					onClick={handleSubmit}
					disabled={isSubmiting || !isFormValid}
				>
					{!selectedBanner ? 'Cadastrar' : 'Salvar'}
				</Button>
			</Container>
		</Modal>
	)
}

export default ModalBanner
